/**
 * `?demo=1` fixtures for the stock Dividend tab, in exactly the contract's `stock/<tradingitem_id>`
 * shape (TED `docs/subsystems/dividends_api.md`): Apple from the contract and the design mockup, and
 * Walgreens with a high chance of a cut and a late declaration. Example data, never live figures.
 */
import { DivAnnual, DivEventRow, DivLadderRow, DivPayment, DivStockResponse } from "@/models/dividends-v2";
import { divSnakeKeys } from "@/controllers/dividends-v2";
import { redactForecast } from "../fixtures";
import { withDemoEvolution } from "./evolutionData";
import { imperialPayload } from "./historyFixtures";
import realApple from "./real-unpublished-stock-2590360.json";

type RowInput = Partial<DivEventRow> & Pick<DivEventRow, "event_key" | "period_label">;

const eventRow = (base: Partial<DivEventRow>, row: RowInput): DivEventRow => ({
    id: `${base.tradingitem_id}:${row.event_key}`,
    tradingitem_id: 0,
    ticker: null,
    company_name: null,
    exchange_symbol: null,
    security_id: null,
    country: "US",
    sector: null,
    market_cap_usd: null,
    kind: "regular",
    frequency: "quarterly",
    declaration_date: null,
    ex_date: null,
    record_date: null,
    pay_date: null,
    declaration_band_days: null,
    ex_band_days: null,
    date_confidence: null,
    currency: "USD",
    last_amount: null,
    amount_p10: null,
    amount_p50: null,
    amount_p90: null,
    change_pct: null,
    p_raise: null,
    p_hold: null,
    p_cut: null,
    p_omit: 0,
    p_special: 0,
    outcome: null,
    outcome_probability: null,
    options_implied: null,
    forward_yield_pct: null,
    status: "forecast",
    days_late: null,
    declared_amount: null,
    declared_at: null,
    declared_vs_forecast: null,
    streak_years: null,
    drivers: null,
    locked: false,
    ...base,
    ...row,
});

// ─── Apple ────────────────────────────────────────────────────────────────────

const AAPL = { tradingitem_id: 2590360, ticker: "AAPL", company_name: "Apple Inc.", exchange_symbol: "Nasdaq", sector: "Information Technology", market_cap_usd: 5.1e12, streak_years: 14, forward_yield_pct: 0.31 };

type LadderSpec = [string, string, string, string, string, number, number, number, number, number | null, number, string, number];
// event_key, label, declaration, ex, pay, p10, p50, p90, last, declared (past), probability of the outcome, outcome, declaration band

/**
 * The four-year horizon (TED #2458): the events after the next eight, on the usual cycle, with a
 * range that widens with the years and no probabilities. event_key, label, declaration, ex, pay, p10, p50, p90.
 */
const AAPL_LATER: [string, string, string, string, string, number, number, number][] = [
    ["2028-Q4", "Q4 FY28", "2028-10-26", "2028-11-06", "2028-11-09", 0.26, 0.28, 0.31],
    ["2029-Q1", "Q1 FY29", "2029-01-25", "2029-02-05", "2029-02-08", 0.26, 0.28, 0.31],
    ["2029-Q2", "Q2 FY29", "2029-04-26", "2029-05-07", "2029-05-10", 0.26, 0.29, 0.32],
    ["2029-Q3", "Q3 FY29", "2029-07-26", "2029-08-06", "2029-08-09", 0.26, 0.29, 0.32],
    ["2029-Q4", "Q4 FY29", "2029-10-25", "2029-11-05", "2029-11-08", 0.26, 0.29, 0.33],
    ["2030-Q1", "Q1 FY30", "2030-01-24", "2030-02-04", "2030-02-07", 0.25, 0.29, 0.34],
    ["2030-Q2", "Q2 FY30", "2030-04-25", "2030-05-06", "2030-05-09", 0.25, 0.3, 0.35],
    ["2030-Q3", "Q3 FY30", "2030-07-25", "2030-08-05", "2030-08-08", 0.25, 0.3, 0.35],
    ["2030-Q4", "Q4 FY30", "2030-10-24", "2030-11-04", "2030-11-07", 0.25, 0.3, 0.36],
];

/** Apple's next eight events (the ladder's future rows, and the quarterly view's payments to come). */
const AAPL_NEXT: LadderSpec[] = [
    ["2026-Q4", "Q4 FY26", "2026-10-30", "2026-11-09", "2026-11-12", 0.26, 0.26, 0.27, 0.26, null, 0.91, "hold", 1],
    ["2027-Q1", "Q1 FY27", "2027-01-28", "2027-02-08", "2027-02-11", 0.26, 0.26, 0.27, 0.26, null, 0.89, "hold", 2],
    ["2027-Q2", "Q2 FY27", "2027-04-29", "2027-05-10", "2027-05-13", 0.26, 0.27, 0.28, 0.26, null, 0.78, "raise", 3],
    ["2027-Q3", "Q3 FY27", "2027-07-29", "2027-08-09", "2027-08-12", 0.26, 0.27, 0.28, 0.27, null, 0.9, "hold", 3],
    ["2027-Q4", "Q4 FY27", "2027-10-28", "2027-11-08", "2027-11-11", 0.26, 0.27, 0.28, 0.27, null, 0.9, "hold", 3],
    ["2028-Q1", "Q1 FY28", "2028-01-27", "2028-02-07", "2028-02-10", 0.26, 0.27, 0.29, 0.27, null, 0.88, "hold", 4],
    ["2028-Q2", "Q2 FY28", "2028-04-27", "2028-05-08", "2028-05-11", 0.27, 0.28, 0.29, 0.27, null, 0.74, "raise", 4],
    ["2028-Q3", "Q3 FY28", "2028-07-27", "2028-08-07", "2028-08-10", 0.27, 0.28, 0.29, 0.28, null, 0.88, "hold", 5],
];

const appleLadder = (): DivLadderRow[] => {
    const future = AAPL_NEXT;
    const past: LadderSpec[] = [
        ["2026-Q3", "Q3 FY26", "2026-07-30", "2026-08-10", "2026-08-13", 0.26, 0.26, 0.27, 0.26, 0.26, 0.9, "hold", 0],
        ["2026-Q2", "Q2 FY26", "2026-05-01", "2026-05-11", "2026-05-14", 0.25, 0.26, 0.27, 0.25, 0.26, 0.81, "raise", 0],
        ["2026-Q1", "Q1 FY26", "2026-01-29", "2026-02-09", "2026-02-12", 0.25, 0.25, 0.26, 0.25, 0.25, 0.93, "hold", 0],
        ["2025-Q4", "Q4 FY25", "2025-10-30", "2025-11-10", "2025-11-13", 0.25, 0.25, 0.26, 0.25, 0.25, 0.92, "hold", 0],
    ];
    const forecastEx: Record<string, string> = { "2026-Q3": "2026-08-10", "2026-Q2": "2026-05-12", "2026-Q1": "2026-02-09", "2025-Q4": "2025-11-10" };
    const build = ([key, label, decl, ex, pay, p10, p50, p90, last, declared, prob, outcome, band]: LadderSpec, isPast: boolean): DivLadderRow => {
        const raise = outcome === "raise" ? prob : (1 - prob) * 0.75;
        const hold = outcome === "hold" ? prob : (1 - prob) * 0.85;
        const cut = Math.max(0, 1 - raise - hold);
        return {
            ...eventRow(AAPL, {
                event_key: key,
                period_label: label,
                declaration_date: decl,
                ex_date: ex,
                record_date: ex,
                pay_date: pay,
                declaration_band_days: isPast ? null : band,
                ex_band_days: isPast ? null : band,
                date_confidence: isPast ? 1 : 0.95 - band * 0.05,
                last_amount: last,
                amount_p10: p10,
                amount_p50: p50,
                amount_p90: p90,
                change_pct: Math.round(((p50 - last) / last) * 1000) / 10,
                p_raise: raise,
                p_hold: hold,
                p_cut: cut,
                p_special: 0.004,
                outcome: outcome as DivEventRow["outcome"],
                outcome_probability: prob,
                options_implied: isPast ? null : p50,
                status: isPast ? "declared" : "forecast",
                declared_amount: declared,
                declared_at: isPast ? `${decl}T20:30:00Z` : null,
                declared_vs_forecast: isPast ? (declared === p50 ? "exact" : (declared ?? 0) > p50 ? "above" : "below") : null,
            }),
            past: isPast,
            forecast_as_of: isPast ? null : "2026-10-14",
            forecast_ex_date: isPast ? forecastEx[key] : null,
        };
    };
    const horizonRow = ([key, label, decl, ex, pay, p10, p50, p90]: (typeof AAPL_LATER)[number]): DivLadderRow => ({
        ...eventRow(AAPL, {
            event_key: key,
            period_label: label,
            declaration_date: decl,
            ex_date: ex,
            record_date: ex,
            pay_date: pay,
            ex_band_days: key.startsWith("2030") ? 6 : 5,
            amount_p10: p10,
            amount_p50: p50,
            amount_p90: p90,
            p_omit: null,
            p_special: null,
        }),
        past: false,
        horizon: true,
        forecast_as_of: null,
    });
    return [...future.map((s) => build(s, false)), ...AAPL_LATER.map(horizonRow), ...past.map((s) => build(s, true))];
};

const AAPL_BORROW: [number, number][] = (() => {
    // 90 days to 2026-10-13, flat around 0.83% of free float with no record-date spike.
    const end = Date.UTC(2026, 9, 13);
    const out: [number, number][] = [];
    for (let i = 89; i >= 0; i--) {
        const t = end - i * 86_400_000;
        const d = new Date(t).getUTCDay();
        if (d === 0 || d === 6) continue;
        out.push([t, Math.round((0.83 + Math.sin(i / 6) * 0.02 + Math.cos(i / 2.3) * 0.01) * 1000) / 1000]);
    }
    return out;
})();

export const demoApple = (): DivStockResponse => {
    const ladder = appleLadder();
    return {
        tradingitem_id: AAPL.tradingitem_id,
        ticker: "AAPL",
        exchange_symbol: "Nasdaq",
        company_name: "Apple Inc.",
        currency: "USD",
        as_of: "2026-10-14",
        locked: false,
        upgrade: null,
        next: { ...ladder[0], drivers: [{ label: "Raises come with May results", direction: "up" }] },
        outlook: { p_raise: 0.07, p_hold: 0.91, p_cut: 0.02, p_omit: 0.0, p_special: 0.004, next_raise: { event_key: "2027-Q2", ex_date: "2027-05-10", amount: 0.27, probability: 0.78 } },
        yield: { forward_12m_pct: 0.31, forward_12m_amount: 1.05, trailing_12m_pct: 0.3, trailing_12m_amount: 1.03, sector_median_pct: 0.9, sector: "Information Technology", streak_years: 14, payments_per_year: 4 },
        pattern: "Declared with Q4 results, ex-date the second Monday of November for 9 years",
        commentary: [
            {
                title: "Timing",
                text: "Apple is expected to declare $0.26 a share on 30 October with its fiscal Q4 results and go ex on 9 November. The last 36 regular dividends were declared with results and went ex within one business day of the expected date; every raise since 2013 came in May.",
            },
            {
                title: "Coverage",
                text: "The payout is well covered. The forward dividend of $1.05 a share is 14% of expected earnings and 11% of trailing free cash flow, and net cash stands at $4.20 a share.",
            },
            {
                title: "What the market says",
                text: "Options expiring after the ex-date imply $0.26, in line with the forecast. Short interest is 0.83% of free float, with no borrowing spike around the last record date.",
            },
        ],
        ladder,
        history: {
            annual: [
                [2016, 0.55],
                [2017, 0.6],
                [2018, 0.68],
                [2019, 0.75],
                [2020, 0.795],
                [2021, 0.85],
                [2022, 0.9],
                [2023, 0.94],
                [2024, 0.98],
                [2025, 1.01],
                [2026, 1.03],
            ]
                .map(([year, amount]): DivAnnual => ({ year, amount, forecast: false, amount_p10: null, amount_p90: null }))
                .concat([
                    // The four-year horizon (TED #2458): central total, and a range that widens with the years.
                    { year: 2027, amount: 1.07, forecast: true, amount_p10: 1.04, amount_p90: 1.11, paying_share: 0.99 },
                    { year: 2028, amount: 1.11, forecast: true, amount_p10: 1.04, amount_p90: 1.19, paying_share: 0.98 },
                    { year: 2029, amount: 1.15, forecast: true, amount_p10: 1.03, amount_p90: 1.27, paying_share: 0.96 },
                    { year: 2030, amount: 1.19, forecast: true, amount_p10: 1.01, amount_p90: 1.37, paying_share: 0.94 },
                ]),
            // `history.quarterly`: each regular dividend (four a year), then the events to come at the central forecast.
            quarterly: [2023, 2024, 2025, 2026]
                .flatMap((year, i) => {
                    const q = [0.235, 0.24, 0.25, 0.26][i];
                    return ["02-09", "05-12", "08-11", "11-09"].map((md, k): DivPayment => ({ period_label: `Q${k + 1} ${year}`, ex_date: `${year}-${md}`, amount: k === 0 && i > 0 ? [0.235, 0.24, 0.25, 0.26][i - 1] : q, forecast: false }));
                })
                .filter((p) => (p.ex_date ?? "") <= "2026-08-11")
                // The next eight events, then the horizon's, so the payments to come run on without a gap.
                .concat(AAPL_NEXT.map(([, label, , ex, , p10, p50, p90]): DivPayment => ({ period_label: label, ex_date: ex, amount: p50, forecast: true, amount_p10: p10, amount_p90: p90 })))
                .concat(AAPL_LATER.map(([, label, , ex, , p10, p50, p90]) => ({ period_label: label, ex_date: ex, amount: p50, forecast: true, amount_p10: p10, amount_p90: p90 }))),
            growth_1y_pct: 2.0,
            growth_5y_cagr_pct: 5.3,
            raises_in_a_row: 14,
            cuts_since_2012: 0,
            specials: 0,
        },
        coverage: { payout_earnings_fwd_pct: 14.0, payout_fcf_pct: 11.0, payout_fcf_after_buybacks_pct: null, net_cash_per_share: 4.2, cover_multiple: 7.1 },
        policy: {
            type: "progressive",
            quote: "We plan to raise the dividend annually",
            source_label: "Q2 FY26 call, 1 May 2026",
            source_url: null,
            last_change_pct: 4.0,
            last_change_date: "2026-05-01",
            guidance_amount: null,
            guidance_text: null,
        },
        borrow: { series: AAPL_BORROW, last_record_date: "2026-08-10", short_interest_pct_ff: 0.83, utilisation_pct: 3.1, ctb_pct: 0.29, record_spike: false },
        options: [
            { expiry: "2026-11-20", implied: 0.26, forecast: 0.26 },
            { expiry: "2027-03-19", implied: 0.52, forecast: 0.52 },
            { expiry: "2027-06-18", implied: 0.8, forecast: 0.79 },
            { expiry: "2027-12-17", implied: 1.33, forecast: 1.33 },
        ],
        ex_behavior: { drop_ratio: 0.9, runup_10d_pct: 0.4, short_volume_after_pct: null, capture_return_pct: -0.02, ex_dates: 12 },
        lists: [
            { name: "Nasdaq US Dividend Achievers", status: "member", note: "10-year rule" },
            { name: "S&P 500 Dividend Aristocrats", status: "not yet", note: "11 years to go" },
        ],
        peers: [
            { ticker: "MSFT", exchange_symbol: "Nasdaq", company_name: "Microsoft", security_id: 1, next_amount: 0.91, next_declaration_date: "2026-11-17", outcome: "hold" },
            { ticker: "CSCO", exchange_symbol: "Nasdaq", company_name: "Cisco", security_id: 2, next_amount: 0.41, next_declaration_date: "2026-11-12", outcome: "hold" },
            { ticker: "AVGO", exchange_symbol: "Nasdaq", company_name: "Broadcom", security_id: 3, next_amount: 0.65, next_declaration_date: "2026-12-11", outcome: "raise" },
            { ticker: "ORCL", exchange_symbol: "NYSE", company_name: "Oracle", security_id: 4, next_amount: 0.5, next_declaration_date: "2026-12-09", outcome: "hold" },
        ],
        scorecard: { amount_exact: 23, ex_exact: 22, n: 24, since: "2020-01-01" },
    };
};

// ─── Walgreens: a high chance of a cut and a late declaration ───────────────────

const WBA = { tradingitem_id: 2600011, ticker: "WBA", company_name: "Walgreens Boots Alliance", exchange_symbol: "Nasdaq", sector: "Consumer Staples", market_cap_usd: 9.6e9, streak_years: 0, forward_yield_pct: 3.4 };

export const demoWalgreens = (): DivStockResponse => {
    const next: DivLadderRow = {
        ...eventRow(WBA, {
            event_key: "2026-Q4",
            period_label: "Q4 2026",
            declaration_date: "2026-10-08",
            ex_date: "2026-10-20",
            record_date: "2026-10-20",
            pay_date: "2026-11-10",
            declaration_band_days: 3,
            ex_band_days: 5,
            date_confidence: 0.55,
            last_amount: 0.25,
            amount_p10: 0.0,
            amount_p50: 0.125,
            amount_p90: 0.25,
            change_pct: -50,
            p_raise: 0.01,
            p_hold: 0.44,
            p_cut: 0.38,
            p_omit: 0.15,
            p_special: 0.0,
            outcome: "hold",
            outcome_probability: 0.44,
            options_implied: 0.11,
            status: "late",
            days_late: 4,
            drivers: [
                { label: "Free cash flow below the dividend", direction: "down" },
                { label: "Net debt rising", direction: "down" },
                { label: "Cut once already in 2024", direction: "down" },
            ],
        }),
        past: false,
        forecast_as_of: "2026-10-14",
    };
    const later: DivLadderRow[] = [
        ["2027-Q1", "Q1 2027", "2027-01-08", "2027-01-19", "2027-02-10", 0.0, 0.125, 0.25, 0.4, "hold"],
        ["2027-Q2", "Q2 2027", "2027-04-09", "2027-04-20", "2027-05-11", 0.0, 0.125, 0.25, 0.46, "hold"],
        ["2027-Q3", "Q3 2027", "2027-07-09", "2027-07-20", "2027-08-10", 0.0, 0.125, 0.25, 0.47, "hold"],
    ].map(([key, label, decl, ex, pay, p10, p50, p90, prob, outcome]) => ({
        ...eventRow(WBA, {
            event_key: key as string,
            period_label: label as string,
            declaration_date: decl as string,
            ex_date: ex as string,
            record_date: ex as string,
            pay_date: pay as string,
            declaration_band_days: 5,
            ex_band_days: 6,
            last_amount: 0.125,
            amount_p10: p10 as number,
            amount_p50: p50 as number,
            amount_p90: p90 as number,
            change_pct: 0,
            p_raise: 0.02,
            p_hold: prob as number,
            p_cut: (1 - (prob as number)) * 0.7,
            p_omit: (1 - (prob as number)) * 0.28,
            outcome: outcome as DivEventRow["outcome"],
            outcome_probability: prob as number,
        }),
        past: false,
        forecast_as_of: "2026-10-14",
    }));
    const past: DivLadderRow[] = [
        ["2026-Q3", "Q3 2026", "2026-07-10", "2026-07-21", "2026-08-12", 0.25, 0.25, "2026-07-21"],
        ["2026-Q2", "Q2 2026", "2026-04-09", "2026-04-21", "2026-05-12", 0.25, 0.25, "2026-04-20"],
        ["2026-Q1", "Q1 2026", "2026-01-09", "2026-01-20", "2026-02-11", 0.25, 0.25, "2026-01-20"],
        ["2025-Q4", "Q4 2025", "2025-10-10", "2025-10-21", "2025-11-12", 0.25, 0.25, "2025-10-23"],
    ].map(([key, label, decl, ex, pay, p50, declared, fEx]) => ({
        ...eventRow(WBA, {
            event_key: key as string,
            period_label: label as string,
            declaration_date: decl as string,
            ex_date: ex as string,
            record_date: ex as string,
            pay_date: pay as string,
            last_amount: 0.25,
            amount_p10: 0.2,
            amount_p50: p50 as number,
            amount_p90: 0.25,
            change_pct: 0,
            p_raise: 0.02,
            p_hold: 0.7,
            p_cut: 0.2,
            p_omit: 0.08,
            outcome: "hold",
            outcome_probability: 0.7,
            status: "declared",
            declared_amount: declared as number,
            declared_at: `${decl}T12:00:00Z`,
            declared_vs_forecast: "exact",
        }),
        past: true,
        forecast_as_of: null,
        forecast_ex_date: fEx as string,
    }));
    return {
        tradingitem_id: WBA.tradingitem_id,
        ticker: "WBA",
        exchange_symbol: "Nasdaq",
        company_name: "Walgreens Boots Alliance",
        currency: "USD",
        as_of: "2026-10-14",
        locked: false,
        upgrade: null,
        next,
        outlook: { p_raise: 0.01, p_hold: 0.44, p_cut: 0.38, p_omit: 0.15, p_special: 0.0, next_raise: null },
        yield: { forward_12m_pct: 3.4, forward_12m_amount: 0.5, trailing_12m_pct: 6.8, trailing_12m_amount: 1.0, sector_median_pct: 2.6, sector: "Consumer Staples", streak_years: 0, payments_per_year: 4 },
        pattern: "Declared in the second week of the quarter's first month, ex-date about 11 days later",
        commentary: null,
        ladder: [next, ...later, ...past],
        history: {
            annual: [
                [2016, 1.5],
                [2017, 1.58],
                [2018, 1.66],
                [2019, 1.79],
                [2020, 1.85],
                [2021, 1.89],
                [2022, 1.91],
                [2023, 1.92],
                [2024, 1.25],
                [2025, 1.0],
                [2026, 1.0],
            ]
                .map(([year, amount]) => ({ year, amount, forecast: false }))
                .concat([{ year: 2027, amount: 0.5, forecast: true }]),
            growth_1y_pct: 0,
            growth_5y_cagr_pct: -11.6,
            raises_in_a_row: 0,
            cuts_since_2012: 1,
            specials: 0,
        },
        coverage: { payout_earnings_fwd_pct: 118.0, payout_fcf_pct: 142.0, payout_fcf_after_buybacks_pct: 142.0, net_cash_per_share: -9.8, cover_multiple: 0.7 },
        policy: null,
        borrow: {
            series: AAPL_BORROW.map(([t, v], i, all) => [t, Math.round((7.6 + v * 0.8 + (i > all.length - 18 && i < all.length - 12 ? 2.4 : 0)) * 100) / 100] as [number, number]),
            last_record_date: "2026-07-21",
            short_interest_pct_ff: 8.4,
            utilisation_pct: 41.0,
            ctb_pct: 1.8,
            record_spike: true,
        },
        options: [
            { expiry: "2026-11-20", implied: 0.11, forecast: 0.125 },
            { expiry: "2027-01-15", implied: 0.2, forecast: 0.25 },
        ],
        ex_behavior: { drop_ratio: 1.1, runup_10d_pct: -0.6, short_volume_after_pct: 18.0, capture_return_pct: -0.4 },
        lists: null,
        peers: [
            { ticker: "CVS", exchange_symbol: "NYSE", company_name: "CVS Health", security_id: 11, next_amount: 0.665, next_declaration_date: "2026-12-04", outcome: "hold" },
            { ticker: "KR", exchange_symbol: "NYSE", company_name: "Kroger", security_id: 12, next_amount: 0.35, next_declaration_date: "2026-12-11", outcome: "hold" },
        ],
        scorecard: { amount_exact: 21, ex_exact: 17, n: 24, since: "2020-01-01" },
    };
};

/**
 * The fixture for a stock page in demo mode: Walgreens on WBA (or with `&example=walgreens`, since WBA
 * is no longer listed), Imperial Brands on IMB, Apple everywhere else. A fund (an ETF,
 * `type !== 1`) gets the Apple payload without the company-only blocks, as an ETF payload would come.
 */
export const demoStockPayload = (ticker: string | null | undefined, isFund: boolean): DivStockResponse => {
    // Past rows carry their path spark and origin, the track record its counts (TED #2456).
    // Imperial Brands (`/s/LSE/IMB/dividend?demo=1`) carries a record back to 1999 (TED #2503).
    const symbol = (ticker ?? "").toUpperCase();
    const base = withDemoEvolution(symbol === "WBA" ? demoWalgreens() : symbol === "IMB" ? imperialPayload(demoApple()) : demoApple());
    if (!isFund) return base;
    return { ...base, commentary: null, coverage: null, policy: null, ex_behavior: null, lists: null, peers: null, borrow: null };
};

const lockRow = <T extends DivEventRow>(r: T): T => ({
    ...r,
    last_amount: null,
    amount_p10: null,
    amount_p50: null,
    amount_p90: null,
    options_implied: null,
    declared_amount: null,
    p_raise: null,
    p_hold: null,
    p_cut: null,
    p_omit: null,
    p_special: null,
    outcome_probability: null,
    change_pct: null,
    forward_yield_pct: null,
    drivers: null,
    locked: true,
});

/**
 * What a customer gets before this listing's forecasts are published (`&unpublished=1`, TED #2467,
 * `unpublished.gate_stock_payload`): the declared ladder rows with their forecast side nulled, `next`
 * the next declared one, no outlook, scorecard or options, no forward yield or sector median, no
 * forward payout, history without forecast entries, peers without their forecasts.
 */
export const unpublishedPayload = (p: DivStockResponse): DivStockResponse => {
    const ladder = (p.ladder ?? []).filter((r) => r.status === "declared").map((r) => ({ ...redactForecast(r), forecast_as_of: null, forecast_ex_date: null, forecast_declaration_date: null, origin: null, evolution: null }));
    const today = p.as_of ?? "";
    const next = ladder.find((r) => !r.past && (r.ex_date ?? "") >= today) ?? null;
    return {
        ...p,
        published: false,
        published_countries: [],
        next,
        ladder: ladder.length ? ladder : null,
        outlook: null,
        scorecard: null,
        options: null,
        yield: p.yield ? { ...p.yield, forward_12m_pct: null, forward_12m_amount: null, sector_median_pct: null } : null,
        coverage: p.coverage ? { ...p.coverage, payout_earnings_fwd_pct: null } : null,
        history: p.history
            ? {
                  ...p.history,
                  annual: p.history.annual.filter((a) => !a.forecast),
                  quarterly: (p.history.quarterly ?? []).filter((q) => !q.forecast),
              }
            : null,
        peers: p.peers ? p.peers.map((peer) => ({ ...peer, next_amount: null, next_declaration_date: null, outcome: null })) : null,
        pattern: next ? p.pattern : null,
        commentary: null,
    };
};

/** `&example=real`: production's answer to a signed-out visitor for Apple on 2026-10-15 (unpublished and locked), through the live key handling. */
export const realStockPayload = (): DivStockResponse => divSnakeKeys(realApple) as DivStockResponse;

/**
 * What a Visitor, Free or Basic caller gets (`&example=locked`): dates, status, frequency and the
 * leading outcome stay; amounts, probabilities and the stock blocks below the tiles are null.
 */
export const lockedPayload = (p: DivStockResponse): DivStockResponse => ({
    ...p,
    locked: true,
    upgrade: "advanced",
    next: p.next ? lockRow(p.next) : null,
    outlook: p.outlook ? { p_raise: null, p_hold: null, p_cut: null, p_omit: null, p_special: null, next_raise: null } : null,
    yield: p.yield ? { ...p.yield, forward_12m_pct: null, forward_12m_amount: null } : null,
    ladder: p.ladder ? p.ladder.map((r) => ({ ...lockRow(r), evolution: null })) : null,
    commentary: null,
    history: null,
    coverage: null,
    policy: null,
    borrow: null,
    options: null,
    ex_behavior: null,
    lists: null,
    peers: null,
    // The listing's track record is public, as the section's is (TED #2456).
    scorecard: p.scorecard,
});
